/**
 * Canonical B2B onboarding checklist — the standard path to a first live
 * behavioral signal in under 48 hours, with no engineering intervention.
 *
 * Single source of truth so any onboarding surface (dashboard checklist,
 * empty states, emails) references the same steps and copy. Each step exposes
 * an `isComplete(ctx)` predicate evaluated against real workspace state, so the
 * checklist reflects actual progress rather than a static list.
 *
 * ctx shape (all optional; unknown fields are treated as "not done"):
 *   {
 *     verified: boolean,            // account email verified
 *     subscription: string,        // 'free' | 'pro' | 'business'
 *     uploadsCount: number,        // datasets uploaded
 *     scoredCustomers: number,     // customers with a computed signal
 *     whoToCallCount: number,      // items in the who-to-call queue
 *     actionsWithOutcome: number,  // actions closed with a recorded outcome
 *   }
 */

export const ONBOARDING_STEPS = [
  {
    key: 'create_workspace',
    title: 'Create your firm workspace',
    description: 'Register with your business email and verify the account.',
    cta: 'Verify account',
    href: '/register',
    isComplete: (ctx) => Boolean(ctx.verified),
  },
  {
    key: 'download_template',
    title: 'Download the minimal data template',
    description:
      'Start from the validated CSV template. Only 9 core fields are required (customer id, country, age, risk profile, knowledge, experience, and portfolio allocation); everything else is optional and marked as such.',
    cta: 'Download template',
    href: '/uploadYourFile',
    isComplete: (ctx) => (ctx.uploadsCount || 0) > 0,
  },
  {
    key: 'upload_dataset',
    title: 'Upload your customer dataset',
    description:
      'Import preview shows actionable errors before anything is saved. Missing optional fields are flagged, not silently defaulted into signals.',
    cta: 'Upload dataset',
    href: '/uploadYourFile',
    isComplete: (ctx) => (ctx.uploadsCount || 0) > 0,
  },
  {
    key: 'scoring',
    title: 'Review your first scored signals',
    description:
      'Scoring runs automatically after upload. Each signal shows its evidence, coverage, and freshness — signals without evidence are marked unavailable, never invented.',
    cta: 'Open insights',
    href: '/dashboard',
    isComplete: (ctx) => (ctx.scoredCustomers || 0) > 0,
  },
  {
    key: 'who_to_call',
    title: 'Work your Who-to-call queue',
    description:
      'A scannable queue ranks who needs a conversation, why, and with what evidence. Assign an owner and a due date.',
    cta: 'Open Who to call',
    href: '/dashboard/rm?view=today',
    isComplete: (ctx) => (ctx.whoToCallCount || 0) > 0,
  },
  {
    key: 'record_outcome',
    title: 'Close the loop with an outcome',
    description:
      'Record what happened on at least one action (contacted, scheduled, converted…). This is your first documented outcome and the start of longitudinal learning.',
    cta: 'Record an outcome',
    href: '/dashboard/rm?view=today',
    isComplete: (ctx) => (ctx.actionsWithOutcome || 0) > 0,
  },
];

export const computeOnboardingProgress = (ctx = {}) => {
  const total = ONBOARDING_STEPS.length;
  const done = ONBOARDING_STEPS.filter((step) => {
    try {
      return step.isComplete(ctx);
    } catch (e) {
      return false;
    }
  }).length;
  return {
    done,
    total,
    pct: total ? Math.round((done / total) * 100) : 0,
    complete: done === total,
    nextStep: ONBOARDING_STEPS.find((step) => {
      try {
        return !step.isComplete(ctx);
      } catch (e) {
        return true;
      }
    }) || null,
  };
};

// Time-to-value target used in copy and success criteria.
export const TIME_TO_FIRST_SIGNAL_TARGET_HOURS = 48;
